import { useLocation } from 'wouter'
import PriceTracker from '../components/PriceTracker'
import SendSwapReceiveCombo from '../components/SendSwapReceiveCombo'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Copy, RefreshCw, Sparkles, ArrowRight, Coins, ScanQrCode, Cookie, Ticket, Users, Wallet } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import Header from '../components/Header'
import { usePrices } from '@/hooks/use-prices'
import { useUser } from '@/hooks/use-user'
import { useToast } from '@/hooks/use-toast'
import WalletQRModal from '@/components/WalletQRModal'
import topLine from '@/assets/home-top-line.svg'
import bottomLine from '@/assets/home-bottom-line.svg'
import star from '@/assets/home-star.svg'
import spaceship from '@/assets/home-ss.svg'
import sheild from '@/assets/home-sheild.svg'
import NavigationTabs from '@/components/common/NavigationTabs'
import { useAccount } from 'wagmi'
import metamask from '@/assets/meta-mask.svg'



export default function WalletPage() {
	const [, navigate] = useLocation()
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [lastRefreshTime, setLastRefreshTime] = useState(0)
	const [, setCopiedAddress] = useState<string | null>(null)
	const [canShowRefresh, setCanShowRefresh] = useState(true);
	const [qrOpen, setQrOpen] = useState(false)

	const {
		uid,
		displayName,
		isConnected,
		loading: userDataLoading,
		walletAddress,
		balance,
		isConnectingWallet,
		walletError,
		connectWallet,
		disconnectWallet,
		updateBalance,
		profileImageUrl
	} = useUser()
	const { toast } = useToast()
	const { convertTokenToUsd } = usePrices()
	const { connector } = useAccount()
	const connectorName = connector?.name

	function walletAvatar(connectorName?: string) {
		if (!connectorName) return null

		if (connectorName.toLowerCase() === 'metamask') {
			return metamask
		}

		return null
	}

	// Enhanced refresh handling with rate limiting and debouncing
	const handleRefreshBalance = useCallback(async () => {
		if (!walletAddress || isRefreshing) return;

		setCanShowRefresh(false); // hide button

		const now = Date.now();
		if (now - lastRefreshTime < 5000) return;

		setIsRefreshing(true);
		setLastRefreshTime(now);
		await updateBalance();
		setIsRefreshing(false);

		// Show button after 5 seconds
		setTimeout(() => {
			setCanShowRefresh(true);
		}, 5000);
	}, [walletAddress, isRefreshing, lastRefreshTime, updateBalance]);

	const copyToClipboard = async (address: string) => {
		try {
			await navigator.clipboard.writeText(address)
			setCopiedAddress(address)
			toast({
				title: 'Address Copied',
				description: 'Address has been copied to clipboard'
			})
			setTimeout(() => setCopiedAddress(null), 2000)
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to copy address',
				variant: 'destructive'
			})
		}
	}

	// Automatic balance refresh on page load and wallet connection
	useEffect(() => {
		if (walletAddress) {
			handleRefreshBalance()
		}
	}, [walletAddress, handleRefreshBalance])

	// Poll balance less frequently when the page is visible
	useEffect(() => {
		let intervalId: NodeJS.Timeout

		if (walletAddress && document.visibilityState === 'visible') {
			intervalId = setInterval(handleRefreshBalance, 60000) // Every 60 seconds
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && walletAddress) {
				handleRefreshBalance()
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			clearInterval(intervalId)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [walletAddress, handleRefreshBalance])

	// Show login screen if no user
	if (!uid) {
		return (
			<div className="max-w-sm mx-auto bg-gray-50 min-h-auto">
				<Header />
				<div className="p-4">
					<Card className="p-6 text-center">
						<h2 className="text-2xl font-bold mb-4">
							Welcome to Secrets Hideaway
						</h2>
						<p className="mb-6">
							You are not authorized yet. Please log in to continue.
						</p>
						<div className="space-y-4">
							<Button
								variant="outline"
								className="w-full"
								onClick={() => navigate('/login')}>
								Login with Email
							</Button>
						</div>
					</Card>
				</div>
			</div>
		)
	}

	// Show connect wallet screen if no wallet
	if (!walletAddress) {
		return (
			<div className="max-w-sm mx-auto bg-gray-50 min-h-auto">
				<Header />
				<div className="p-4">
					<Card className="p-6 text-center">
						<h2 className="text-2xl font-bold mb-4">
							Connect Your Wallet
						</h2>
						<p className="mb-6 text-gray-600">
							Connect your wallet to view your portfolio and start trading.
						</p>
						<Button
							onClick={connectWallet}
							className="w-full"
							size="lg"
							disabled={isConnectingWallet}>
							{isConnectingWallet ? (
								<>
									<RefreshCw className="mr-2 h-4 w-4 animate-spin" />
									Connecting...
								</>
							) : (
								'Connect Your Wallet'
							)}
						</Button>

						{walletError && (
							<Alert variant="destructive" className="mt-4">
								<AlertDescription>{walletError}</AlertDescription>
							</Alert>
						)}
					</Card>
				</div>
			</div>
		)
	}

	return (
		<div className="w-full bg-[#18181B] min-h-auto ">
			<div className="fixed top-0 z-10 backdrop-blur-[24px] w-full">
				<Header />
			</div>
			<div className="max-w-5xl mx-auto px-5 pb-8">
				{/* Portfolio Card */}
				<div className="hero-banner h-[258px] mb-9 rounded-b-[36px] relative w-full px-5 bg-[linear-gradient(180deg,_#000000_0%,_#EF4444_55.8%)]">

					<img className='absolute bottom-5 left-5' src={spaceship} alt="" />
					<img className='absolute bottom-5 right-4' src={star} alt="" />
					<img className='absolute top-[50px] right-0' src={topLine} alt="" />
					<img className='absolute bottom-0 left-0' src={bottomLine} alt="" />
					<img className='absolute top-[137px] right-5' src={sheild} alt="" />
					<div className='h-[280px] w-[280px] bg-[#EF4444] blur-[110px] absolute left-1/2 -translate-x-1/2 top-[118px] rounded-[140px]'></div>

					<div className='absolute left-1/2 -translate-x-1/2 bottom-[35px] w-[calc(100%-40px)]'>
						<div className="flex items-center justify-start mb-[26px] gap-2">
							{/* Profile Circle */}
							<div className="h-[38px] w-[38px] rounded-full flex items-center justify-center
									bg-[#FFFFFF33]
									text-white font-montserrat font-semibold text-[18px]">
								{profileImageUrl ? (
									<img
										src={profileImageUrl}
										alt="Profile"
										className="relative h-10 w-10 rounded-full object-cover"
									/>
								) : (
									displayName?.charAt(0).toUpperCase()
								)}
							</div>

							{/* Text */}
							<span className="flex flex-col justify-between h-9">
								<strong className="text-[20px] leading-[18px] font-bold font-montserrat text-[#ffffff]">
									Hi {displayName?.split(' ')[0]}!
								</strong>
								<small className="text-[12px] leading-[10px] font-normal font-montserrat text-[#ffffff]">
									Welcome to your Wallet!
								</small>
							</span>
						</div>

						<div className='w-full flex flex-col justify-center gap-3'>
							<label className='text-sm leading-4 font-normal font-montserrat text-[#ffffff] text-center'>Portfolio Value</label>
							<div className='hb-price-outer h-[58px] mw-auto mx-auto gap-[26px] px-[10px] flex items-center justify-between rounded-[12px] shadow-[0_8px_26px_0_#00000066] bg-[linear-gradient(174.83deg,_#EF4444_9.48%,_#761414_98.75%)]'>
								<span className="flex justify-between items-end gap-3">
									<strong className="text-[42px] leading-[52px] font-semibold font-montserrat text-[#ffffff]">{(balance || 0).toFixed(2)}</strong>
									<small className="hb-sech-name text-[12px] font-semibold leading-[28px] font-montserrat text-[#ffffff]">Sech</small>
								</span>
								<button className='h-[34px] flex items-center justify-center rounded-[17px] text-[#EF4444] bg-white text-sm leading-[18px] font-semibold font-figtree' onClick={() => navigate('/buy-tokens')}>Buy</button>
							</div>
						</div>
					</div>

					<data className='h-[42px] absolute left-1/2 -translate-x-1/2 bottom-[-21px] px-8 text-sm leading-[26px] font-montserrat font-medium text-[#ffffff] rounded-[21px] flex justify-center items-center backdrop-blur-[44px] shadow-[0px_8px_36px_0px_rgba(0,0,0,0.4)] bg-[linear-gradient(92.62deg,_#18181B_-0.88%,_#464649_98.1%)]' value="">
						${convertTokenToUsd(balance || 0, 'sech').toFixed(2)}
					</data>

				</div>

				{/* Wallet Details Section */}

				<div className='mb-4 bg-[linear-gradient(180deg,_rgba(239,68,68,0.2)_0%,_rgba(255,255,255,0.2)_100%)] rounded-[16px] p-[1px] mx-3'>
					<section className="p-3 flex flex-col gap-3 bg-[linear-gradient(180deg,_rgba(47,47,50,0.2)_0%,_rgba(24,24,27,0.4)_100%)] rounded-[17px] backdrop-blur-[12px]" aria-labelledby="wallet-address-title">
						<div className="p-3 rounded-[12px] flex items-center justify-between gap-3 bg-[rgba(255,255,255,0.1)]">
							<div className="address-row flex flex-col gap-2 w-[calc(100%-54px)]">
								<p id="" className="address-label font-montserrat text-sm leading-4 font-medium text-[#ffffff] mb-0">
									Full Address
								</p>
								<code className="address-value break-all font-figtree text-xs leading-4 font-normal text-[#ffffff]">
									{walletAddress}
								</code>
							</div>
							<button
								type="button"
								className="copy-btn h-[42px] w-[42px] flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.1)] text-[#ffffff] cursor-pointer"
								aria-label="Copy wallet address"
								onClick={() => copyToClipboard(walletAddress)}
							>
								<Copy className='h-[22px] w-[22px] text-[#ffffff]' />
							</button>
						</div>

						<div className="utility-actions bg-[rgba(255,255,255,0.1)] px-4 h-[42px] flex items-center justify-between rounded-[21px] qr-container" role="group" aria-label="Wallet utilities">
							<button type="button" className="utility-btn flex items-center gap-2 w-1/2 connect-wallet-btn" onClick={() => setQrOpen(true)}>
								<ScanQrCode className='h-[22px] w-[22px] text-[#ffffff] connect-wallet-btn-icons' /> <span className='text-xs leading-4 font-normal text-[#ffffff]'>QR Code</span>
							</button>

							<span className="divider w-[1px] h-8 bg-[linear-gradient(180deg,_#783739_1.56%,_rgba(255,255,255,0.4)_49.08%,_#783739_100%)]" aria-hidden="true"></span>

							<button
								type="button"
								className="flex items-center gap-2 w-1/2 pl-4 justify-start connect-wallet-btn"
								onClick={isConnected ? disconnectWallet : connectWallet}
								disabled={isConnectingWallet}
							>
								{walletAvatar(connectorName) ? (
									<img
										src={walletAvatar(connectorName)!}
										alt={connectorName}
										className="h-[22px] w-[22px]"
									/>
								) : (
									<Wallet className="h-[22px] w-[22px] text-white connect-wallet-btn-icons" />
								)}

								<span className="text-xs leading-4 font-normal text-white">
									{isConnectingWallet
										? isConnected
											? "Disconnecting..."
											: "Connecting..."
										: isConnected
											? "Disconnect"
											: "Connect Wallet"
									}
								</span>
							</button>
						</div>
						<SendSwapReceiveCombo />

					</section>
				</div>

				{/* Buy SECH Tokens CTA Banner */}
				<div className="mx-3 mb-4">
					<button
						onClick={() => navigate('/buy-tokens')}
						className="w-full bg-gradient-to-r from-[#dc2626] via-orange-500 to-amber-500 hover:from-[#b91c1c] hover:via-orange-600 hover:to-amber-600 text-white rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
					>
						{/* Animated shine effect */}
						<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

						<div className="relative flex items-center justify-between">
							<div className="flex items-center gap-4">
								<div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
									<Coins className="h-7 w-7" />
								</div>
								<div className="text-left">
									<div className="flex items-center gap-2">
										<span className="font-bold text-xl">Buy SECH Tokens</span>
										<Sparkles className="h-5 w-5 text-yellow-200" />
									</div>
									<div className="text-white/90 text-sm">Grow your balance & unlock exclusive benefits</div>
								</div>
							</div>
							<ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
						</div>
					</button>
				</div>

				<section
					className="mx-3 rounded-2xl mb-8 p-3 text-white
                           bg-red-500
                           bg-cover bg-center
                           relative overflow-hidden buy-tokens"
				>
					<div className="relative">
						<h3 className="text-[18px] leading-[22px] font-semibold font-montserrat mb-3">
							Buy with Tokens at Secrets Hideaway
						</h3>

						<ul className="buy-token-card grid-cols-3 gap-y-2 gap-x-3 text-sm font-medium max-xs:grid-cols-3 rounded-sm p-2 backdrop-filter-blur-[12px] bg-[rgba(255,255,255,0.1)] flex text-[12px] gap-[2px]">
							<li className="flex items-center gap-2 h-[28px] text-[12px] px-1 cursor-pointer" onClick={() => navigate("/membership")}>
								<Users className='h-[18px] w-[18px]' />
								<span className='text-xs leading-[14px] font-medium font-montserrat break-all w-[calc(100%-26px)] text-[#ffffff]'>Membership</span>
							</li>

							<li className="flex items-center text-[12px] gap-2 h-[28px] px-1 cursor-pointer" onClick={() => navigate("/entry-passes")}>
								<Ticket className='h-[18px] w-[18px]' />
								<span className='text-xs leading-[14px] font-medium font-montserrat break-all w-[calc(100%-26px)] text-[#ffffff]'>Passes</span>
							</li>

							<li className="flex text-[12px] items-center gap-2 h-[28px] px-1 cursor-pointer" onClick={() => navigate("/menu")}>
								<Cookie className='h-[18px] w-[18px]' />
								<span className='text-xs leading-[14px] font-medium font-montserrat break-all w-[calc(100%-26px)] text-[#ffffff]'>Food Items</span>
							</li>
						</ul>
					</div>
				</section>

				<section className="market-trend mx-3 mb-7" aria-labelledby="market-trend-title">

					<h2 id="market-trend-title" className="market-title mb-3 text-sm leading-5 font-semibold text-[#ffffff] font-montserrat">
						Market Trend
					</h2>
					<PriceTracker />
				</section>
			</div>
			<WalletQRModal
				open={qrOpen}
				onOpenChange={setQrOpen}
				walletAddress={walletAddress}
			/>

			{/* Wallet Error Display */}
			{walletError && (
				<div className="px-4 pb-4">
					<Alert variant="destructive">
						<AlertDescription>{walletError}</AlertDescription>
					</Alert>
				</div>
			)}

			{/* {isConnected && (
				<div className="px-4 pb-6">
					<Button
						onClick={disconnectWallet}
						variant="outline"
						className="w-full"
					>
						Disconnect Wallet
					</Button>
				</div>
			)} */}
			<NavigationTabs />
		</div>
	)

}